import { useContext, useEffect, useMemo } from "react";

import api from "api";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { buildUiPalettes } from "../ui/utils/uiPalette";
import { colorMap } from "./constants";
import { SizeContext } from "./sizeContext";
import type { UseThemeProps } from "./types";
import { loadGoogleFont } from "./utils";

export const useBuildTheme = (props?: UseThemeProps) => {
  const sizes = useContext(SizeContext);
  if (!sizes) {
    throw new Error("useBuildTheme must be used within a SizeProvider");
  }

  const me = useCurrentUser(api);
  const primaryColour =
    props?.organisation?.primaryColour ||
    me?.ownerOrganisation?.primaryColour ||
    "#14435B";
  const secondaryColour =
    props?.organisation?.secondaryColour ||
    me?.ownerOrganisation?.secondaryColour ||
    "#1b678e";
  const fontFamily = useMemo(
    () =>
      props?.organisation?.fontFamily ||
      me?.ownerOrganisation?.fontFamily || {
        h1: "Inter",
        h2: "Inter",
        h3: "Inter",
        h4: "Inter",
        h5: "Inter",
        body: "Inter",
      },
    [props?.organisation?.fontFamily, me?.ownerOrganisation?.fontFamily],
  );

  useEffect(() => {
    const fontFamilies = [
      fontFamily.h1,
      fontFamily.h2,
      fontFamily.h3,
      fontFamily.h4,
      fontFamily.h5,
      fontFamily.body,
    ];
    // @ts-ignore -- Set is iterable
    const uniqueFontFamilies = [...new Set(fontFamilies)];
    const validFontFamilies = uniqueFontFamilies.filter(
      (family): family is string => Boolean(family),
    );
    validFontFamilies.forEach((family) => {
      loadGoogleFont(family, [400, 500, 600, 700]);
    });
  }, [fontFamily]);

  const fullTheme = useMemo(() => {
    return {
      sizes,
      primaryColour,
      secondaryColour,
      colors: buildUiPalettes({ colorMap, primaryColour, secondaryColour }),
      fontFamily,
    };
  }, [sizes, primaryColour, secondaryColour, fontFamily]);

  return fullTheme;
};
